import React from "react";
import { fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Droplet, CheckCircle2, MapPin, ChevronDown, ChevronUp, LocateFixed, CalendarDays, Phone, Package, Navigation } from "lucide-react";

/* Presentational pieces of DriverTicketCard, extracted to keep the card itself small. */

const getCloseLabel = (busy, showPrices, subtotalLabel) => {
  if (busy) return "Closing…";
  if (showPrices) return `Close & invoice · ${subtotalLabel}`;
  return "Close ticket";
};

/** Collapsed summary header — customer, badges, address, gallons/items line. */
export const DriverCardHeader = ({ t, open, onToggle, routeIndex, distanceMiles, firstAddr, isProductsOnly, items, pools, totalGallons, subtotal, showPrices }) => {
  const customerLabel = t.business_name || t.customer_name;
  return (
    <button onClick={onToggle} className="w-full text-left px-4 py-3 flex items-start justify-between gap-3" data-testid={`driver-card-toggle-${t.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {Number.isFinite(routeIndex) && (
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-sm bg-primary text-primary-foreground text-xs font-bold mono-num flex-shrink-0" data-testid={`driver-route-index-${t.id}`}>
              {routeIndex + 1}
            </span>
          )}
          <span className="font-display text-lg font-bold leading-tight">{customerLabel}</span>
          {t.assigned_driver_name && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-success/15 text-success text-[10px] font-bold uppercase tracking-wider" data-testid={`driver-assigned-${t.id}`}>
              {t.assigned_driver_name}
            </span>
          )}
          {t.refill_empty_jugs && !isProductsOnly && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider" data-testid={`driver-jugs-${t.id}`}>
              <Droplet className="w-3 h-3" /> Jugs
            </span>
          )}
        </div>
        {t.delivery_date && (
          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1" data-testid={`driver-date-${t.id}`}>
            <CalendarDays className="w-3 h-3 flex-shrink-0" />
            <span className="font-mono">Scheduled {fmtDate(t.delivery_date)}</span>
          </div>
        )}
        {firstAddr && (
          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{firstAddr}</span>
          </div>
        )}
        <div className="text-xs text-muted-foreground mt-1">
          {isProductsOnly ? (
            <span className="inline-flex items-center gap-1 font-semibold text-foreground" data-testid={`driver-products-only-${t.id}`}>
              <Package className="w-3 h-3" /> Products only · {items.length} item{items.length === 1 ? "" : "s"}{showPrices ? ` · ${fmtMoney(subtotal)}` : ""}
            </span>
          ) : (
            <>{pools.length} pool{pools.length === 1 ? "" : "s"} · <span className="mono-num">{totalGallons}</span> gal{showPrices ? ` · ${fmtMoney(subtotal)}` : ""}</>
          )}
          {Number.isFinite(distanceMiles) && (
            <span className="ml-2 inline-flex items-center gap-1 text-primary font-semibold">
              <LocateFixed className="w-3 h-3" /> {distanceMiles.toFixed(1)} mi
            </span>
          )}
        </div>
      </div>
      {open ? <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
    </button>
  );
};

/** Tap-to-call contact row + Drive button (when there are no pool sections). */
export const DriverCardActions = ({ t, pools, firstAddr }) => (
  <>
    {(t.contact_phone || t.contact_name) && (
      <div className="flex items-center gap-2" data-testid={`driver-contact-${t.id}`}>
        {t.contact_phone ? (
          <a
            href={`tel:${String(t.contact_phone).replace(/[^+\d]/g, "")}`}
            className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-sm bg-success text-white font-semibold text-sm tracking-wide hover:bg-success/90 transition-colors"
            data-testid={`driver-call-${t.id}`}
          >
            <Phone className="w-4 h-4" />
            <span className="truncate">Call {t.contact_name || "customer"} · {t.contact_phone}</span>
          </a>
        ) : (
          <div className="flex-1 px-3 py-2 rounded-sm bg-secondary text-xs text-muted-foreground" data-testid={`driver-no-phone-${t.id}`}>
            Contact: {t.contact_name || "(no name on file)"} — no phone on file
          </div>
        )}
      </div>
    )}
    {pools.length === 0 && firstAddr && (
      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(firstAddr)}`}
         target="_blank" rel="noopener noreferrer"
         className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-sm bg-foreground text-background font-semibold text-sm tracking-wide hover:bg-foreground/90 transition-colors"
         data-testid={`driver-drive-${t.id}`}>
        <Navigation className="w-4 h-4" />
        <span className="truncate">Drive · {firstAddr}</span>
      </a>
    )}
  </>
);

/** "Products to deliver" list shown on tickets with product items. */
export const DriverItemsList = ({ ticketId, items }) => (
  <div className="border border-border rounded-sm p-3 space-y-1" data-testid={`driver-items-${ticketId}`}>
    <div className="font-semibold uppercase tracking-wider text-[10px] text-muted-foreground flex items-center gap-1">
      <Package className="w-3 h-3" /> Products to deliver
    </div>
    {items.map((it, i) => (
      <div key={`${it.product_id}-${i}`} className="flex items-center justify-between text-sm">
        <span>{it.name || "Product"}</span>
        <span className="mono-num font-semibold">{Number(it.quantity)}×</span>
      </div>
    ))}
  </div>
);

/** Gallons confirmation, photo warning and the Close button. */
export const DriverCloseSection = ({ t, isProductsOnly, confirmed, setConfirmed, missingPhotoPools, photosOk, busy, showPrices, subtotal, onSubmit }) => (
  <>
    {!isProductsOnly && (
      <label className="flex items-start gap-3 border border-primary/40 bg-primary/5 rounded-sm px-3 py-3 cursor-pointer" data-testid={`driver-confirm-block-${t.id}`}>
        <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={confirmed}
               onChange={(e) => setConfirmed(e.target.checked)} data-testid={`driver-confirm-${t.id}`} />
        <div className="text-sm">
          <div className="font-bold text-primary">I confirm the gallons above</div>
          <div className="text-xs text-muted-foreground">Required to close this ticket and generate the invoice.</div>
        </div>
      </label>
    )}
    {!photosOk && (
      <div className="border border-destructive/40 bg-destructive/5 rounded-sm px-3 py-2 text-xs text-destructive font-mono" data-testid={`driver-photo-warning-${t.id}`}>
        Before AND after photos required for: {missingPhotoPools.map((p) => p.pool_name || "(unnamed)").join(", ")}
      </div>
    )}
    <Button onClick={onSubmit} disabled={busy || (!confirmed && !isProductsOnly) || !photosOk} className="rounded-sm w-full h-12 text-base font-semibold"
            data-testid={`driver-close-${t.id}`}>
      <CheckCircle2 className="w-4 h-4 mr-2" />
      {getCloseLabel(busy, showPrices, fmtMoney(subtotal))}
    </Button>
  </>
);
